import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/* ------------------------------------------------------------------ */
/* Entrance: each marked part rises once as its section comes into view */
/* ------------------------------------------------------------------ */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      )
    : null;

function observeReveals(root = document) {
  if (!revealObserver) return;
  for (const element of root.querySelectorAll('[data-sk-reveal]:not(.is-in)')) {
    revealObserver.observe(element);
  }
}

observeReveals();
window.SoftKeepReady = true;

// Sections re-rendered in the theme editor get observed again.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) observeReveals(event.target);
});

/* ------------------------------------------------------------------ */
/* Cart: add lines and let Horizon's drawer and cart icon update       */
/* ------------------------------------------------------------------ */

/**
 * @param {{ id: string, quantity: number }[]} lines
 * @param {HTMLElement} source
 */
async function addLinesToCart(lines, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
        sections: sectionIds.join(','),
      }),
    });
    const result = await response.json();
    const cart = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    if (result.status) {
      source.dispatchEvent(
        new CartErrorEvent({ error: result.description || result.message || 'Add to bag failed', code: 'INVALID' })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'softkeep', itemCount },
      });
      throw new Error(result.description || result.message || 'Add to bag failed');
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'softkeep',
        sourceId: source.id || 'softkeep',
        itemCount,
        sections: result.sections,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Any form marked data-sk-add posts to /cart/add without JavaScript.
 * With JavaScript it adds in place, shows the state on its button and
 * opens the cart drawer.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-sk-add')) return;
  event.preventDefault();

  const lines = [];
  for (const input of form.querySelectorAll('[data-sk-line]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (input.type === 'checkbox' && !input.checked) continue;
    if (input.disabled || !input.value) continue;
    lines.push({ id: input.value, quantity: Number(input.dataset.quantity || 1) });
  }
  if (lines.length === 0) return;

  const button = form.querySelector('button[type="submit"]');
  const error = form.querySelector('[data-sk-error]');
  if (error) error.textContent = '';
  if (button instanceof HTMLButtonElement) {
    button.dataset.state = 'loading';
    button.setAttribute('aria-disabled', 'true');
  }

  try {
    await addLinesToCart(lines, form);
    if (button instanceof HTMLButtonElement) button.dataset.state = 'added';
    setTimeout(() => {
      if (button instanceof HTMLButtonElement) {
        button.dataset.state = '';
        button.removeAttribute('aria-disabled');
      }
    }, 2200);
  } catch (caught) {
    if (button instanceof HTMLButtonElement) {
      button.dataset.state = '';
      button.removeAttribute('aria-disabled');
    }
    if (error) error.textContent = caught instanceof Error ? caught.message : String(caught);
  }
});

/* ------------------------------------------------------------------ */
/* Routine builder: totals the chosen steps against free shipping       */
/* ------------------------------------------------------------------ */

class SoftKeepRoutine extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.addEventListener('change', () => this.update(), { signal: this.#controller.signal });
    this.update();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  update() {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const threshold = Number(this.dataset.threshold || 0);

    let total = 0;
    let count = 0;
    for (const input of this.querySelectorAll('input[type="checkbox"][data-sk-line]')) {
      if (!(input instanceof HTMLInputElement) || input.disabled) continue;
      const step = input.closest('[data-sk-step]');
      step?.toggleAttribute('data-chosen', input.checked);
      if (!input.checked) continue;
      total += Number(input.dataset.price || 0);
      count += 1;
    }

    const totalOutput = this.querySelector('[data-sk-total]');
    if (totalOutput) totalOutput.textContent = formatMoney(total, format, currency);

    const remaining = Math.max(threshold - total, 0);
    const message = this.querySelector('[data-sk-shipping]');
    if (message) {
      if (count === 0) {
        message.textContent = this.dataset.textEmpty || '';
      } else if (remaining > 0) {
        message.textContent = (this.dataset.textAway || '').replace('[amount]', formatMoney(remaining, format, currency));
      } else {
        message.textContent = this.dataset.textFree || '';
      }
    }

    const meter = this.querySelector('[data-sk-meter]');
    if (meter instanceof HTMLElement && threshold > 0) {
      const ratio = Math.min(total / threshold, 1);
      meter.style.setProperty('--sk-progress', String(ratio));
      meter.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    }

    const countOutput = this.querySelector('[data-sk-count]');
    if (countOutput) countOutput.textContent = String(count);

    const submit = this.querySelector('button[type="submit"]');
    if (submit instanceof HTMLButtonElement) submit.disabled = count === 0;
  }
}

if (!customElements.get('sk-routine')) customElements.define('sk-routine', SoftKeepRoutine);

/* ------------------------------------------------------------------ */
/* Tabs: one product at a time, keyboard friendly                      */
/* ------------------------------------------------------------------ */

class SoftKeepTabs extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.tabs[0];
    if (selected) this.select(selected);
    else this.querySelector('[data-plate]')?.setAttribute('data-active', '');

    this.addEventListener(
      'click',
      (event) => {
        const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
        if (tab instanceof HTMLElement) this.select(tab);
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        const tabs = this.tabs;
        const current = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
        if (current === -1) return;
        let next = current;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        tabs[next]?.focus();
        if (tabs[next]) this.select(tabs[next]);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /** @returns {HTMLElement[]} */
  get tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {HTMLElement} tab */
  select(tab) {
    this.tabs.forEach((other, index) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel instanceof HTMLElement) panel.hidden = !selected;
      this.querySelector(`[data-plate="${index}"]`)?.toggleAttribute('data-active', selected);
    });
  }
}

if (!customElements.get('sk-tabs')) customElements.define('sk-tabs', SoftKeepTabs);

/* ------------------------------------------------------------------ */
/* Index with preview: pointing at a row shows its picture in one frame */
/* ------------------------------------------------------------------ */

class SoftKeepIndex extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const show = (event) => {
      const row = event.target instanceof Element ? event.target.closest('[data-sk-preview]') : null;
      if (row instanceof HTMLElement) this.show(row.dataset.skPreview || '');
    };
    this.addEventListener('pointerover', show, { signal });
    this.addEventListener('focusin', show, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /** @param {string} key */
  show(key) {
    if (!key) return;
    for (const frame of this.querySelectorAll('[data-sk-frame]')) {
      if (frame instanceof HTMLElement) frame.toggleAttribute('data-active', frame.dataset.skFrame === key);
    }
    for (const row of this.querySelectorAll('[data-sk-preview]')) {
      if (row instanceof HTMLElement) row.toggleAttribute('data-active', row.dataset.skPreview === key);
    }
  }
}

if (!customElements.get('sk-index')) customElements.define('sk-index', SoftKeepIndex);

/* ------------------------------------------------------------------ */
/* Bundle picker: the chosen tier sets the quantity of the product form */
/* ------------------------------------------------------------------ */

/** The tier a shopper chose survives the page re-rendering after a variant change. */
const bundleChoice = new Map();

class SoftKeepBundle extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section');

    this.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        bundleChoice.set(this.#key, input.value);
        this.apply();
      },
      { signal }
    );

    // A new variant re-renders the section; put the choice back afterwards.
    section?.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        event.promise?.then(() => requestAnimationFrame(() => this.restore())).catch(() => {});
      },
      { signal }
    );

    // Horizon resets the quantity after an add; keep it in step with the choice.
    document.addEventListener(StandardEvents.cartLinesUpdate, () => setTimeout(() => this.apply(), 500), { signal });

    this.restore();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  get #key() {
    return this.closest('.shopify-section')?.id || 'product';
  }

  /** @returns {HTMLInputElement[]} */
  get inputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]);
  }

  restore() {
    const inputs = this.inputs;
    const saved = bundleChoice.get(this.#key);
    const fallback = inputs[Number(this.dataset.default || 1) - 1] ?? inputs[0];
    const chosen = inputs.find((input) => input.value === saved) ?? fallback;
    if (chosen) chosen.checked = true;
    this.apply();
  }

  apply() {
    const chosen = this.inputs.find((input) => input.checked);
    for (const input of this.inputs) {
      input.closest('[data-sk-tier]')?.toggleAttribute('data-selected', input === chosen);
    }
    if (!chosen) return;

    const section = this.closest('.shopify-section');
    for (const selector of section?.querySelectorAll('product-form-component quantity-selector-component') ?? []) {
      if (typeof selector.setValue === 'function') selector.setValue(chosen.value);
      else selector.querySelector('input[name="quantity"]')?.setAttribute('value', chosen.value);
    }
  }
}

if (!customElements.get('sk-bundle')) customElements.define('sk-bundle', SoftKeepBundle);

/* ------------------------------------------------------------------ */
/* Payment icons: the "+4" button shows the rarer cards                 */
/* ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-sk-pay-toggle]') : null;
  if (!button) return;
  const wrap = button.closest('sk-pay');
  if (!wrap) return;
  const open = wrap.toggleAttribute('data-open');
  button.setAttribute('aria-expanded', String(open));
});

/* ------------------------------------------------------------------ */
/* Add-on: a ticked partner product joins the bag with the main product */
/* ------------------------------------------------------------------ */

class SoftKeepAddon extends HTMLElement {
  #controller = new AbortController();
  #adding = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.#onAdd(event), {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  async #onAdd(event) {
    const input = this.querySelector('[data-sk-addon-input]');
    if (this.#adding || !(input instanceof HTMLInputElement) || !input.checked) return;

    // Only the main product form of this section, never our own add.
    const source = event.target;
    if (!(source instanceof Element) || !source.closest('product-form-component')) return;
    if (source.closest('.shopify-section') !== this.closest('.shopify-section')) return;

    try {
      const result = await event.promise;
      if (result?.detail?.didError) return;
    } catch {
      return;
    }

    this.#adding = true;
    try {
      await addLinesToCart([{ id: this.dataset.variantId || '', quantity: 1 }], this);
      input.checked = false;
    } catch {
      // The bag already holds the main product; the add-on can be added again.
    } finally {
      this.#adding = false;
    }
  }
}

if (!customElements.get('sk-addon')) customElements.define('sk-addon', SoftKeepAddon);
